import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useField } from "formik";
import { ReactNode } from "react";

interface InputTextProps {
  name: string;
  label: ReactNode;
  moreinfo?: string;
  type?: string;
  ssn?: boolean;
  autocomplete?: string;
  maxLength?: number;
  numbersOnly?: boolean;
  upperCase?: boolean;
}

const InputText = ({
  name,
  label,
  moreinfo,
  type = "text",
  ssn,
  autocomplete = "off",
  maxLength,
  numbersOnly = false,
  upperCase = false,
}: InputTextProps) => {
  const [field, meta, helpers] = useField(name);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (ssn) {
      const ssnVal = e.target.value;
      if (ssnVal && ssnVal.length === 10) {
        const formattedSsn = `${ssnVal.substring(0, 6)}-${ssnVal.substring(6)}`;
        helpers.setValue(formattedSsn);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    if (numbersOnly) {
      value = value.replace(/[^0-9]/g, ""); // Remove any non-numeric characters
    }

    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength); // Enforce maxLength
      e.target.value = value; // Explicitly set the DOM value for cross-platform consistency
    }

    helpers.setValue(value); // Update Formik state
  };

  const handleClearField = () => {
    helpers.setValue("");
  };

  return (
    <>
      <div className="input-container">
        <label htmlFor={name} className="required">
          <span>{label}</span>
          {moreinfo && (
            <span className="more-info">
              <span aria-hidden="true">{moreinfo}</span>
              {ssn && (
                <span className="show-for-sr">
                  i formatet, år, månad, dag och de fyra sista siffrorna.
                </span>
              )}
            </span>
          )}
          {meta.error && (
            <span className="validation-error">
              {meta.error.toString()}{" "}
              {ssn && (
                <>
                  <span aria-hidden="true">(ÅÅMMDD-XXXX)</span>
                  <span className="show-for-sr">
                    i formatet, år, månad, dag och de fyra sista siffrorna.
                  </span>
                </>
              )}
            </span>
          )}
        </label>
        <div
          className={`input-wrapper ${ssn ? "short" : "long"} ${meta.error ? "has-error" : ""}`}
        >
          <input
            required
            className={`${ssn ? "short" : "long"} ${meta.error ? "has-error" : ""} ${upperCase ? "uppercase" : ""}`}
            type={type}
            onBlur={handleBlur}
            onChange={handleInputChange}
            id={name}
            name={name}
            value={field.value}
            autoComplete={autocomplete}
            maxLength={maxLength}
            {...(type === "number" ? { inputMode: "numeric" } : {})}
          />
          {(field.value ?? "").length > 0 && (
            <button
              className="clear-button"
              onClick={handleClearField}
              type="button"
            >
              <div className="show-for-sr">Rensa fältet, {label}</div>
              <FontAwesomeIcon icon={solid("times")} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default InputText;
